import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { resolveAppVersion, versionTooltip, type AppVersion } from "@/lib/app-version";

/**
 * Build identity of the running bundle, shown two ways in the sidebar header:
 * as a chip under the logo and title, and as a tooltip on the title itself.
 *
 * Every decision is in src/lib/app-version.ts, including which builds get a
 * chip. Renders nothing when the bundle carries no build identity: local
 * `vite dev`, CI's throwaway build, and Lovable's production build, which runs
 * no CI and reads the committed .env.
 */

/**
 * Both tooltips are ambient: you read the header on the way to somewhere else,
 * and a tooltip that appears while the pointer is crossing it is noise. A
 * second is long enough to mean "you stopped here".
 *
 * Set per tooltip rather than on the sidebar's TooltipProvider, which also
 * backs the nav-item labels and keeps its own immediate delay.
 */
const TOOLTIP_DELAY_MS = 1000;

function useAppVersion(): AppVersion | null {
  // Static member access on purpose. Vite rewrites `import.meta.env.VITE_FOO`
  // literally and leaves a computed lookup undefined in the bundle, so a
  // refactor to `import.meta.env[key]` would blank every badge in production
  // while passing every unit test — vitest's import.meta.env is a real object.
  // Same note in src/lib/preview-auth-shim.ts. The `VITE_APP_*` values are
  // asserted to reach a built bundle in .github/workflows/ci.yml.
  return resolveAppVersion({
    env: import.meta.env.VITE_APP_ENV as string | undefined,
    version: import.meta.env.VITE_APP_VERSION as string | undefined,
    commit: import.meta.env.VITE_APP_COMMIT as string | undefined,
    prNumber: import.meta.env.VITE_APP_PR_NUMBER as string | undefined,
    releaseLine: import.meta.env.VITE_APP_RELEASE_LINE as string | undefined,
  });
}

/**
 * The chip: "v1.0+pr-54" on a preview, "dev" on dev. Hovering names the commit
 * it was built from; the chip links to that build's PR or branch.
 *
 * Staging and prod show nothing here and carry the same identity in the title
 * tooltip instead. Not tied to the tenant switcher below it: the build has
 * nothing to do with which tenant is selected, and that switcher renders
 * nothing until its tenant query resolves.
 */
export function VersionBadge() {
  const version = useAppVersion();
  if (!version?.showChip) return null;

  return (
    <Tooltip delayDuration={TOOLTIP_DELAY_MS}>
      <TooltipTrigger asChild>
        <a
          href={version.href}
          target="_blank"
          rel="noreferrer noopener"
          className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Badge variant="outline" className="font-mono">
            {version.label}
          </Badge>
        </a>
      </TooltipTrigger>
      <TooltipContent className="text-center text-xs">
        <div className="text-muted-foreground">{version.target}</div>
        {/* Prefixed because a bare hash does not say what it is. */}
        {version.commit ? <div className="font-mono">sha:{version.commit}</div> : null}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Names the running build when you hover the logo and title. Present on every
 * deploy target, so a release can be identified without a chip in the chrome.
 *
 * Text only. No link and no commit: those are the chip's, and only the builds
 * that show one have them.
 */
export function VersionTooltip({ children }: { children: ReactNode }) {
  const version = useAppVersion();
  if (!version) return <>{children}</>;

  const [appName, buildLabel] = versionTooltip(version.label);

  return (
    <Tooltip delayDuration={TOOLTIP_DELAY_MS}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="text-xs">
        <div>{appName}</div>
        <div>{buildLabel}</div>
      </TooltipContent>
    </Tooltip>
  );
}
